import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

const SOURCE_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
    CHART_COLORS.chart.line6,
    CHART_COLORS.primary,
    CHART_COLORS.secondary,
    CHART_COLORS.accent,
    CHART_COLORS.success,
];

export const RevenueByUTMSourceChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        const ts = finance?.trafficSummary;
        if (!ts?.sources) return [];

        const sources = ts.sources;

        return Object.entries(sources)
            .map(([name, data]) => ({
                name,
                revenue: Number(data.total) || 0,
            }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);
    }, [finance]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={120}
                            tickFormatter={(value: string) =>
                                value.length > 18 ? `${value.slice(0, 18)}...` : value
                            }
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.revenue', 'Revenue'),
                            ]}
                        />
                        <Bar
                            dataKey="revenue"
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((_entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={SOURCE_COLORS[index % SOURCE_COLORS.length]}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
